import {
  Client,
  ClientRepresentationStatusEnum,
  ClientsApi,
  Configuration,
  CreateClientDto,
  PaginatedClientsDto,
  UpdateClientDto,
} from '@sr-sdks/api-sdk-axios';
import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type { RawAxiosRequestConfig } from 'axios';
import { configuration } from '../../configuration';
import axiosInstance, {
  bypassCacheConfig,
  invalidateCacheRequestConfig,
} from '../../utils/axiosInstance';
import { toApiError } from '../../utils/httpError';

export const CLIENTS_DEFAULT_PARAMS = {
  page: 1,
  pageSize: 25,
  sortBy: 'clientName:asc',
} as const satisfies ClientsPaginatedParams;

const clientsQueryKeys = {
  all: ['clients'] as const,
  paginated: (params?: ClientsPaginatedParams) =>
    [...clientsQueryKeys.all, 'paginated', { ...params }] as const,
  single: (clientId?: string) =>
    [...clientsQueryKeys.all, 'single', { clientId }] as const,
};

/**
 * Domain key for this module's paginated queries — derived from the query
 * key factory rather than hand-typed, so consumers that scan the cache by
 * domain (e.g. `useCachedPaginatedItem`) stay in sync with it.
 */
export const CLIENTS_QUERY_DOMAIN_KEY = clientsQueryKeys.all[0];

export interface ClientsPaginatedParams {
  allowPayments?: boolean;
  clientIds?: string[];
  clientName?: string;
  clientReferenceId?: string;
  page?: number;
  pageSize?: number;
  representationEndDateFrom?: string;
  representationEndDateTo?: string;
  representationStartDateFrom?: string;
  representationStartDateTo?: string;
  representationStatus?: ClientRepresentationStatusEnum;
  representationStatuses?: ClientRepresentationStatusEnum[];
  searchTerm?: string;
  shortId?: string;
  sortBy?: string;
  workspaceId?: string;
}

export interface CreateClientParams {
  createClientDto: CreateClientDto;
}

export interface UpdateClientParams {
  id: string;
  updateClientDto: UpdateClientDto;
}

// Get Clients API instance
const GetClientsApi = () => {
  return new ClientsApi(
    new Configuration(),
    configuration.WORKSPACES_API_BASE,
    axiosInstance,
  );
};

// Fetch clients with pagination
export const fetchClientsPaginated = async (
  params: ClientsPaginatedParams,
  options?: RawAxiosRequestConfig,
): Promise<PaginatedClientsDto> => {
  try {
    const response = await GetClientsApi().clientControllerFindAllV2(
      params?.allowPayments,
      params?.clientIds,
      params?.clientName,
      params?.clientReferenceId,
      params?.representationEndDateFrom,
      params?.representationEndDateTo,
      params?.representationStartDateFrom,
      params?.representationStartDateTo,
      params?.representationStatus,
      params?.representationStatuses?.length
        ? params.representationStatuses
        : undefined,
      params?.searchTerm,
      params?.shortId,
      params?.workspaceId,
      params?.page ?? 1,
      params?.pageSize ?? 25,
      params?.sortBy || 'clientName:asc',
      options,
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'clientControllerFindAllV2',
      params: params,
    });
  }
};

// Fetch a single client by ID
export const fetchClientById = async (id: string): Promise<Client> => {
  try {
    const response = await GetClientsApi().clientControllerFindOne(
      id,
      bypassCacheConfig(),
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'clientControllerFindOne',
      params: { id },
    });
  }
};

// Create a new client
export const createClient = async ({
  createClientDto,
}: CreateClientParams): Promise<Client> => {
  try {
    const response = await GetClientsApi().clientControllerCreate(
      createClientDto,
      invalidateCacheRequestConfig('client'),
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'clientControllerCreate',
      params: createClientDto,
    });
  }
};

// Update an existing client
export const updateClient = async ({
  id,
  updateClientDto,
}: UpdateClientParams): Promise<Client> => {
  try {
    const response = await GetClientsApi().clientControllerUpdate(
      id,
      updateClientDto,
      // Every client URL, not just this one: the Dashboard's unconfirmed
      // clients alert reads `fetchClientsPaginated` through the axios cache
      // and would otherwise still show the pre-update status.
      invalidateCacheRequestConfig('client'),
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'clientControllerUpdate',
      params: { id, updateClientDto },
    });
  }
};

// Delete a client by ID
export const deleteClient = async (id: string): Promise<Client> => {
  try {
    const response = await GetClientsApi().clientControllerRemove(
      id,
      invalidateCacheRequestConfig('client'),
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'clientControllerRemove',
      params: { id },
    });
  }
};

const selectClientsPaginated = (
  data: PaginatedClientsDto,
): PaginatedClientsDto => {
  return {
    ...data,
    items: data.items.filter((v) => Boolean(v._id)),
  };
};

export const queryOptionsGetClientsPaginated = (
  params?: ClientsPaginatedParams,
) => {
  const mergedParams: ClientsPaginatedParams = {
    ...CLIENTS_DEFAULT_PARAMS,
    ...params,
  };

  return queryOptions({
    placeholderData: keepPreviousData,
    // React Query is the only cache for this list, so `invalidateQueries`
    // refetches from the server. In the query function, not the fetcher: the
    // Dashboard calls the fetcher directly and keeps the axios cache.
    queryFn: mergedParams.workspaceId
      ? () => fetchClientsPaginated(mergedParams, bypassCacheConfig())
      : skipToken,
    queryKey: clientsQueryKeys.paginated(mergedParams),
    select: selectClientsPaginated,
  });
};

export const queryOptionsGetClient = (clientId?: string) => {
  return queryOptions({
    queryFn: clientId ? () => fetchClientById(clientId) : skipToken,
    queryKey: clientsQueryKeys.single(clientId),
  });
};

// Hook for creating a new client
export const useCreateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createClient,
    onSuccess: () => {
      // Invalidate paginated queries
      queryClient.invalidateQueries({
        queryKey: clientsQueryKeys.paginated(),
      });
    },
  });
};

// Hook for updating a client
export const useUpdateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateClient,
    onSuccess: (data, variables) => {
      // Update the specific client in cache
      queryClient.setQueryData(clientsQueryKeys.single(variables.id), data);

      // Invalidate paginated queries
      queryClient.invalidateQueries({
        queryKey: clientsQueryKeys.paginated(),
      });
    },
  });
};

// Hook for deleting a client
export const useDeleteClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteClient,
    onSuccess: (data, id) => {
      // Remove the client from cache
      queryClient.removeQueries({
        queryKey: clientsQueryKeys.single(id),
      });

      // Invalidate paginated queries
      queryClient.invalidateQueries({
        queryKey: clientsQueryKeys.paginated(),
      });
    },
  });
};
